import React, { useEffect, useState, useRef } from "react";
import { Comment, FloatingCommentsProps } from "./interfaces";
import CommentThread from "@shared/components/EditorJs/comments/CommentThread";
import CommentService from "@shared/components/EditorJs/utils/commentsApi";
import { useParams } from "react-router-dom";

interface GroupedPosition {
  blockId: string;
  comments: Comment[]; // changed from commentsId to full objects
  top: number;
  left: number;
}

const FloatingComments: React.FC<FloatingCommentsProps> = ({
  comments,
  handleReply,
  handleCommentDelete,
}) => {
  const [positions, setPositions] = useState<GroupedPosition[]>([]);
  const [visibleComments, setVisibleComments] = useState<{
    [key: string]: boolean;
  }>({});
  const commentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const { projectId, reportId } = useParams<{
    projectId: string;
    reportId: string;
  }>();

  useEffect(() => {
    const updatePositions = () => {
      const groupedPositions: { [blockId: string]: GroupedPosition } = {};

      comments.forEach((c) => {
        const anchors = c.anchor ?? [];
        let blockId = "";
        let blockElement: HTMLElement | null = null;

        if (anchors.length > 1) {
          for (const anchor of anchors) {
            const id = anchor.includes("|") ? anchor.split("|")[0] : anchor;
            const element = document.querySelector(
              `[data-id='${id}']`
            ) as HTMLElement | null;
            if (element) {
              blockId = id;
              blockElement = element;
              break;
            }
          }
        } else {
          const singleAnchor = anchors[0] ?? "";
          blockId = singleAnchor.includes("|")
            ? singleAnchor.split("|")[0]
            : singleAnchor;
          blockElement = document.querySelector(
            `[data-id='${blockId}']`
          ) as HTMLElement | null;
        }

        if (blockElement) {
          const rect = blockElement.getBoundingClientRect();
          const scrollTop =
            window.scrollY || document.documentElement.scrollTop;

          const mapContainer = document.querySelector(
            "#mapContainerDivReport"
          ) as HTMLElement;
          const mapHeight = mapContainer?.offsetHeight ?? 0;

          const additionalHeader = document.querySelector(
            ".report-additional-header"
          ) as HTMLElement | null;
          const additionalHeight = additionalHeader?.offsetHeight
            ? additionalHeader.offsetHeight + 30
            : 0;

          let top = rect.top + scrollTop - 20 - mapHeight - additionalHeight;
          const left = rect.width + 100;

          const reportHeader = document.querySelector(
            "#reportHeaderDiv"
          ) as HTMLElement;
          const reportHeaderHeight = reportHeader?.offsetHeight ?? 0;
          top = top - reportHeaderHeight; // Adjust top position based on header height

          if (groupedPositions[blockId]) {
            groupedPositions[blockId].comments.push(c);
          } else {
            groupedPositions[blockId] = {
              blockId,
              comments: [c],
              top,
              left,
            };
          }
        } else {
          if (projectId && reportId) {
            CommentService.removeCommentSilently(
              projectId,
              reportId,
              c.id + ""
            );
          }
        }
      });

      const newPositions: GroupedPosition[] = Object.values(groupedPositions);
      setPositions(newPositions);
    };

    updatePositions();
  }, [comments]);

  // Function to toggle visibility of comments
  const toggleComments = (blockId: string) => {
    setVisibleComments((prev) => {
      const isNowVisible = !prev[blockId]; // Get the toggled state

      const blockElement = document.querySelector(
        `[data-id='${blockId}']`
      ) as HTMLElement;

      if (blockElement) {
        blockElement.style.paddingRight = isNowVisible ? "32rem" : "0px"; // Adjust padding as needed
      }

      return {
        ...prev,
        [blockId]: isNowVisible,
      };
    });
  };

  // Click outside logic
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      Object.keys(commentRefs.current).forEach((blockId) => {
        if (
          visibleComments[blockId] &&
          commentRefs.current[blockId] &&
          !commentRefs.current[blockId]?.contains(event.target as Node)
        ) {
          // Reset padding when closing comments
          const blockElement = document.querySelector(
            `[data-id='${blockId}']`
          ) as HTMLElement;

          if (blockElement) {
            blockElement.style.paddingRight = "0px"; // Reset padding
          }

          setVisibleComments((prev) => ({
            ...prev,
            [blockId]: false,
          }));
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [visibleComments]);

  return (
    <>
      {positions.map((comment_position, i) => {
        return (
          <div
            key={comment_position.blockId}
            className="comment absolute flex-col"
            style={{
              top: `${comment_position.top || 0}px`,
              right: 100,
              zIndex: 1,
              position: "absolute",
            }}
            ref={(el) => (commentRefs.current[comment_position.blockId] = el)} // Assign ref
          >
            <div className="comment relative">
              <button
                className="comment flex items-center space-x-1 absolute"
                onClick={() => toggleComments(comment_position.blockId)}
                style={{ right: -50, top: 50 }}
              >
                <img
                  src={
                    visibleComments[comment_position.blockId]
                      ? "/comment_opened.svg"
                      : "/comment_closed.svg"
                  }
                  className="comment w-6"
                  alt=""
                />
                <span className="w-3 text-[#085aca]">
                  {" "}
                  {comment_position.comments.length}{" "}
                </span>
              </button>

              {visibleComments[comment_position.blockId] && (
                <div className="comment-thread-container relative">
                  <CommentThread
                    blockId={comment_position.blockId}
                    commentList={comment_position.comments}
                    handleReply={handleReply}
                    handleCommentDelete={handleCommentDelete}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default FloatingComments;
